/* Step 7a (owner card "AZEX Step 7 Card V2", 29-Oct-2026) — the result of a sample's evaluation and its conclusion.
   Approach B (ratio method, per stratum then summed), approach C (over / under / net; MUS: an upper limit for each side),
   approach A / S14 (actual, not projected); best estimate = projected + items tested 100 % + anomalous, against tolerable
   misstatement (S21); "Reasonable basis: Yes / No — further action" (PUT /samples/{id}/conclusion), signed by Preparer and
   Reviewer 1 on the sampling-sheet sign-off bar; W9 takes the best estimate by a recorded button (S13, OQ-1). */
import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import React, { useState } from "react";
import api, { toErrMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { fmtDateTimeFirm } from "@/lib/dt";
import { amt, cnt, fill } from "@/pages/Sampling/evaluationFormat";

const netOf = (v) => (v == null ? null : (v.net != null ? v.net : Number(v.over || 0) - Number(v.under || 0)));
const Row = ({ label, v, testid, strong }) => (
  <tr className="border-b">
    <td className={`px-2 py-0.5 ${strong ? "font-semibold" : ""}`}>{label}</td>
    {["over", "under", "net"].map((k) => (
      <td key={k} className={`px-2 py-0.5 text-end tabular-nums ${strong ? "font-semibold" : ""}`}
          data-testid={testid ? `${testid}-${k}` : undefined}>{v == null ? "—" : amt(k === "net" ? netOf(v) : v[k])}</td>
    ))}
  </tr>
);

function MusLimit({ side, data, t }) {
  const termCols = useResizableColumns({ storageKey: "ev-mus-terms", defaults: [64, 100, 130, 130, 90, 100, 150], mins: [48, 80, 96, 96, 64, 80, 96] });   // Step 11d · add-on: adjustable columns
  if (!data) return null;
  const parts = [["s7a.panel.mus.basicPrecision", data.basic_precision], ["s7a.panel.mus.topActual", data.top_actual],
                 ["s7a.panel.mus.projected", data.projected], ["s7a.panel.mus.ia", data.incremental_allowance],
                 ["s7a.panel.mus.anomalous", data.anomalous]];
  return (
    <div className="rounded border p-2" data-testid={`ev-mus-${side}`}>
      <div className="font-medium">{t(side === "over" ? "s7a.panel.mus.upperOver" : "s7a.panel.mus.upperUnder")}</div>
      <table className="w-full"><tbody>
        {parts.map(([k, v]) => (
          <tr key={k}><td className="pe-2">{t(k)}</td><td className="text-end tabular-nums" data-testid={`ev-mus-${side}-${k.split(".").pop()}`}>{amt(v || 0)}</td></tr>
        ))}
        <tr className="border-t font-semibold"><td>{t("s7a.panel.mus.uml")}</td>
          <td className="text-end tabular-nums" data-testid={`ev-uml-${side}`}>{amt(data.uml)}</td></tr>
      </tbody></table>
      {(data.terms || []).length > 0 && (
        <ResizableTable cols={termCols} wrap className="mt-1 w-full text-[10px]" data-testid={`ev-mus-${side}-terms`}>
          <thead className="text-muted-foreground"><tr>
            {["rank", "tainting", "book", "projected", "rf", "ia", "source"].map((k, i) => (
              <ResizableTh key={k} index={i} className="px-1 text-end">{t(`s7a.panel.mus.terms.${k}`)}</ResizableTh>))}
          </tr></thead>
          <tbody>{data.terms.map((r) => (
            <tr key={r.rank}>
              <td className="px-1 text-end">{r.rank}</td>
              <td className="px-1 text-end tabular-nums">{Number(r.tainting).toFixed(4)}</td>
              <td className="px-1 text-end tabular-nums">{amt(r.book_value)}</td>
              <td className="px-1 text-end tabular-nums">{amt(r.projected_misstatement)}</td>
              <td className="px-1 text-end tabular-nums">{Number(r.reliability_factor).toFixed(4)}</td>
              <td className="px-1 text-end tabular-nums">{amt(r.incremental_allowance)}</td>
              <td className="px-1 text-end">{t(r.factor_source === "calculated" ? "s7a.panel.mus.calculated" : "s7a.panel.mus.tabulated")}</td>
            </tr>))}</tbody>
        </ResizableTable>
      )}
      {data.reliability_factor_k0 != null && (
        <div className="text-[10px] text-muted-foreground">{fill(t("s7a.panel.mus.rf0"), { rf: Number(data.reliability_factor_k0).toFixed(4) })}</div>
      )}
    </div>
  );
}

function Conclusion({ state, ev, t, locked, onSaved }) {
  const c = state.conclusion;
  const [basis, setBasis] = useState(c?.status === "current" ? c.reasonable_basis : "");
  const [action, setAction] = useState(c?.status === "current" ? c.further_action || "" : "");
  const [busy, setBusy] = useState(false);
  const atOrAbove = !!ev?.vs_tolerable?.at_or_above;
  const required = basis === "no" || atOrAbove;
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/samples/${state.sample_id}/conclusion`, { reasonable_basis: basis, further_action: action });
      toast.success(t("s7a.panel.conclusionSavedToast"));
      onSaved();
    } catch (e) { toast.error(toErrMsg(e, t("s7a.panel.saveFailed"))); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-1 rounded border border-slate-300 p-2" data-testid="ev-conclusion">
      <div className="font-medium">{t("s7a.panel.conclusionTitle")}</div>
      {c && (
        <div className={`rounded p-1 text-[11px] ${c.status === "current" ? "bg-emerald-50 text-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200" : "bg-amber-50 text-amber-900"}`}
             data-testid="ev-conclusion-saved">
          {c.status !== "current" && <div className="font-semibold" data-testid="ev-conclusion-superseded">{t("s7a.conclusionSuperseded")}</div>}
          <div>{t("s7a.panel.reasonableBasis")}: <b>{c.reasonable_basis === "yes" ? t("s7a.panel.yes") : t("s7a.panel.no")}</b>
            {c.further_action ? <> — {t("s7a.panel.furtherAction")}: {c.further_action}</> : null}</div>
          <div className="text-muted-foreground">{fill(t("s7a.panel.conclusionSaved"), { when: fmtDateTimeFirm(c.saved_at), who: c.saved_by || "" })}</div>
          <div data-testid="ev-conclusion-signed">{state.conclusion_signed ? t("s7a.panel.signed") : t("s7a.panel.notSigned")}</div>
        </div>
      )}
      {!locked && (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <span>{t("s7a.panel.reasonableBasis")}</span>
            {["yes", "no"].map((v) => (
              <label key={v} className="inline-flex items-center gap-1">
                <input type="radio" name={`basis-${state.sample_id}`} checked={basis === v} onChange={() => setBasis(v)} data-testid={`ev-basis-${v}`} />
                {t(`s7a.panel.${v}`)}
              </label>
            ))}
          </div>
          <label className="block">{t("s7a.panel.furtherAction")}{required ? " *" : ""}
            <textarea rows={2} className="mt-0.5 w-full rounded border px-2 py-1 dark:bg-slate-900" value={action}
                      onChange={(e) => setAction(e.target.value)} data-testid="ev-further-action" />
          </label>
          {required && <div className="text-[10px] text-amber-800" data-testid="ev-further-required">{t("s7a.panel.furtherActionRequired")}</div>}
          <Button size="sm" onClick={save} disabled={busy || !basis || (required && !action.trim())} data-testid="ev-conclusion-save">
            {t("s7a.panel.saveConclusion")}</Button>
        </>
      )}
    </div>
  );
}

export default function EvaluationResults({ state, t, locked, onChanged }) {
  const strataCols = useResizableColumns({ storageKey: "ev-strata", defaults: [160, 100, 110, 90, 120, 110, 110, 120, 120, 120], mins: [100, 70, 80, 64, 90, 80, 80, 90, 90, 90] });   // Step 11d · add-on: adjustable columns
  const colW = useResizableColumns({ storageKey: "sampling-evaluation-results-ev-best", defaults: [100, 130, 130, 130], mins: [60, 90, 90, 90] });   // Step 11d · add-on
  const ev = state.latest_evaluation;
  const [busy, setBusy] = useState(false);
  if (!ev) return <div className="text-muted-foreground" data-testid="ev-not-evaluated">{t("s7a.panel.notEvaluated")}</div>;
  if (state.earlier_method) {
    const r = ev.result || {};
    return (
      <div className="rounded border border-amber-300 bg-amber-50 p-2 text-amber-900" data-testid="ev-earlier-method">
        <div className="font-semibold">{t("s7a.earlierMethod")}</div>
        <div>{fill(t("s7a.panel.earlierSaved"), { p: amt(r.sample_projected_misstatement), u: amt(r.upper_misstatement_limit),
                                                  when: fmtDateTimeFirm(ev.evaluated_at) })}</div>
      </div>
    );
  }
  const vt = ev.vs_tolerable || {};
  const mus = ev.mus;
  const projected = ev.method_kind === "ratio" || ev.method_kind === "mus";
  const sendW9 = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/samples/${state.sample_id}/w9-best-estimate`);
      toast.success(fill(t("s7a.panel.w9Toast"), { amount: amt(r.data?.amount) }));
      onChanged();
    } catch (e) { toast.error(toErrMsg(e, t("s7a.panel.saveFailed"))); } finally { setBusy(false); }
  };
  const w9 = state.w9_best_estimate;
  return (
    <div className="space-y-2" data-testid="ev-results">
      <div className="text-muted-foreground">{fill(t("s7a.panel.evaluatedAt"), { when: fmtDateTimeFirm(ev.evaluated_at), who: ev.evaluated_by_name || "" })}
        {" · "}{fill(t("s7a.panel.history"), { n: cnt((state.evaluations || []).length) })}</div>
      {ev.method_kind === "ratio" && (ev.strata || []).length > 0 && (
        <div className="overflow-x-auto">
          <div className="font-medium">{t("s7a.panel.strataTitle")}</div>
          <ResizableTable cols={strataCols} className="w-full" data-testid="ev-strata">
            <thead className="border-b text-[10px] uppercase text-muted-foreground"><tr>
              {["stratum", "N_h", "P_h", "n_h", "T_h", "overFound", "underFound", "projOver", "projUnder", "projNet"].map((k, i) => (
                <ResizableTh key={k} index={i} className={`px-2 py-0.5 ${k === "stratum" ? "text-start" : "text-end"}`}>{t(`s7a.panel.strataCols.${k}`)}</ResizableTh>
              ))}
            </tr></thead>
            <tbody>{ev.strata.map((s) => (
              <tr key={s.stratum} className="border-b" data-testid={`ev-stratum-${s.stratum}`}>
                <td className="px-2 py-0.5">{s.stratum}{s.tested_100pct ? ` · ${t("s7a.panel.tested100")}` : ""}{s.anomalies ? ` · ${t("s7a.anomaly")} ${s.anomalies}` : ""}</td>
                <td className="px-2 py-0.5 text-end tabular-nums">{cnt(s.N_h)}</td>
                <td className="px-2 py-0.5 text-end tabular-nums">{amt(s.P_h)}</td>
                <td className="px-2 py-0.5 text-end tabular-nums">{cnt(s.n_h)}</td>
                <td className="px-2 py-0.5 text-end tabular-nums">{amt(s.T_h)}</td>
                <td className="px-2 py-0.5 text-end tabular-nums">{amt(s.over_found)}</td>
                <td className="px-2 py-0.5 text-end tabular-nums">{amt(s.under_found)}</td>
                <td className="px-2 py-0.5 text-end tabular-nums">{s.tested_100pct ? "—" : amt(s.projected_over)}</td>
                <td className="px-2 py-0.5 text-end tabular-nums">{s.tested_100pct ? "—" : amt(s.projected_under)}</td>
                <td className="px-2 py-0.5 text-end tabular-nums">{s.tested_100pct ? "—" : amt(s.projected_net)}</td>
              </tr>
            ))}</tbody>
          </ResizableTable>
        </div>
      )}
      <ResizableTable cols={colW} wrap className="w-full max-w-2xl" data-testid="ev-best">
        <thead className="border-b text-[10px] uppercase text-muted-foreground"><tr>
          <ResizableTh index={0} className="px-2 py-0.5 text-start"></ResizableTh>
          <ResizableTh index={1} className="px-2 py-0.5 text-end">{t("s7a.panel.over")}</ResizableTh>
          <ResizableTh index={2} className="px-2 py-0.5 text-end">{t("s7a.panel.under")}</ResizableTh>
          <ResizableTh index={3} className="px-2 py-0.5 text-end">{t("s7a.panel.net")}</ResizableTh>
        </tr></thead>
        <tbody>
          {projected && <Row label={t("s7a.panel.projected")} v={ev.projected} testid="ev-projected" />}
          <Row label={projected ? t("s7a.panel.topActual") : t("s7a.panel.actualFound")} v={ev.top_stratum_actual} testid="ev-top" />
          {ev.method_kind === "mus" && mus?.most_likely && <Row label={t("s7a.panel.mus.mostLikely")} v={mus.most_likely} testid="ev-most-likely" />}
          <Row label={projected ? t("s7a.panel.anomalous") : t("s7a.panel.anomalousOfWhich")} v={ev.anomalous} testid="ev-anomalous" />
          <Row label={t("s7a.panel.bestEstimate")} v={ev.best_estimate} testid="ev-best-estimate" strong />
        </tbody>
      </ResizableTable>
      {!projected && <div className="text-[11px] text-sky-900" data-testid="ev-not-projected">{t("s7a.panel.notProjected")}</div>}
      {mus && (
        <div className="grid gap-2 md:grid-cols-2">
          <MusLimit side="over" data={mus.upper_limit_over} t={t} />
          <MusLimit side="under" data={mus.upper_limit_under} t={t} />
        </div>
      )}
      <div className={`rounded p-1.5 ${vt.at_or_above ? "bg-red-50 text-red-900 dark:bg-red-950/30 dark:text-red-200" : "bg-emerald-50 text-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200"}`}
           data-testid="ev-vs-tolerable">
        {t("s7a.panel.tolerable")}: <span className="tabular-nums">{vt.tolerable == null ? t("s7a.panel.tmNotSet") : amt(vt.tolerable)}</span>
        {" — "}<b>{vt.at_or_above ? t("s7a.panel.atOrAbove") : t("s7a.panel.below")}</b>
      </div>
      {(ev.counts?.untestable?.count > 0 || ev.counts?.anomalies?.count > 0 || ev.counts?.replaced?.count > 0) && (
        <div className="text-[11px] text-muted-foreground" data-testid="ev-counts">
          {fill(t("s7a.panel.countsLine"), { u: cnt(ev.counts.untestable.count), uv: amt(ev.counts.untestable.value),
                                             r: cnt(ev.counts.replaced.count), a: cnt(ev.counts.anomalies.count) })}
        </div>
      )}
      {projected && !locked && (
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" onClick={sendW9} disabled={busy || state.stale} data-testid="ev-w9-btn">{t("s7a.panel.w9Btn")}</Button>
          <span className="text-[10px] text-muted-foreground">{t("s7a.panel.w9Help")}</span>
        </div>
      )}
      {w9 && (
        <div className="text-[11px]" data-testid="ev-w9-done">{fill(t("s7a.panel.w9Done"), { amount: amt(w9.amount), best: amt(w9.best_estimate),
                                                                                         factual: amt(w9.factual_on_w9), when: fmtDateTimeFirm(w9.at) })}</div>
      )}
      <Conclusion state={state} ev={ev} t={t} locked={locked} onSaved={onChanged} />
    </div>
  );
}
